<template>
    <div class="px-6 py-4 bg-minplan-beige -mb-4">
        <page-title :title="$t('exports.bkpTitle')"></page-title>
    </div>

    <div class="p-6">
        <p class="text-sm text-gray-600 mb-4">
            {{
                hasBuildings
                    ? $t("exports.bkpDescription")
                    : $t("exports.bkpDescriptionNoBuildings")
            }}
        </p>

        <div class="flex justify-between items-center mb-2">
            <span class="text-xs uppercase font-semibold tracking-widest text-gray-500">
                {{ project.name }}
            </span>
            <button
                v-if="selectableUuids.length"
                type="button"
                class="text-xs uppercase font-semibold tracking-widest text-minplan-darkgreybeige"
                @click="toggleAll"
            >
                {{
                    allSelected
                        ? $t("exports.archiveDeselectAll")
                        : $t("exports.archiveSelectAll")
                }}
            </button>
        </div>

        <!-- Gebäude-Baum: Untergruppen eingerückt statt mit wiederholtem
             Elternnamen. Ein Häkchen am Gebäude nimmt seine Untergruppen mit;
             wird eine Untergruppe abgewählt, fallen ihre Wohnungen ganz aus
             dem Export (Entscheid Peter, 31.08.2026). -->
        <ul
            v-if="flatBuildings.length"
            class="divide-y divide-gray-100 border rounded mb-2 max-h-64 overflow-y-auto"
        >
            <li
                v-for="entry in flatBuildings"
                :key="entry.uuid"
                class="flex items-center px-3 py-2"
                :style="{ paddingLeft: 12 + entry.level * 22 + 'px' }"
            >
                <input
                    type="checkbox"
                    class="rounded mr-3"
                    :value="entry.uuid"
                    v-model="selectedBuildings"
                    @change="cascade(entry)"
                />
                <span class="text-sm text-gray-900">{{ entry.name }}</span>
                <span class="ml-auto text-xs text-gray-400">
                    {{ $t("exports.bkpApartmentCount", { count: entry.apartmentCount }) }}
                </span>
            </li>
        </ul>

        <!-- Entscheid Peter, 21.09.2026: ohne Gebäude gibt es nur ein Blatt mit
             den Wohnungen als Spalten; beide Gebäude-Schalter bewirken dort
             nichts und sind ausgeblendet. -->
        <label v-if="hasBuildings && unassignedCount" class="flex items-center mb-4">
            <input type="checkbox" class="rounded mr-2" v-model="withUnassigned" />
            <span class="text-sm text-gray-700">
                {{ $t("exports.bkpWithUnassigned") }}
                <span class="text-xs text-gray-400">
                    ({{ $t("exports.bkpApartmentCount", { count: unassignedCount }) }})
                </span>
            </span>
        </label>

        <p class="text-xs uppercase font-semibold tracking-widest text-gray-500 mb-2">
            {{ $t("exports.bkpDisplay") }}
        </p>

        <label v-if="hasBuildings" class="flex items-start mb-2">
            <input type="checkbox" class="rounded mr-2 mt-1" v-model="subgroupSheets" />
            <span class="text-sm text-gray-700">
                {{ $t("exports.bkpSubgroupSheets") }}
                <span class="block text-xs text-gray-400">
                    {{ $t("exports.bkpSheetCount", { count: sheetCount }) }}
                </span>
            </span>
        </label>

        <!--
            Ohne Gebaeude gibt es die Schaltflaeche darueber nicht, und mit ihr
            verschwand bisher auch der Blattzaehler — obwohl `sheetCount` dort
            korrekt 1 liefert (UI-Gate K-1, 22.09.2026). Die Zeile steht
            deshalb hier eigenstaendig. Bewusst NICHT die obere Fassung
            herausgezogen: die Ansicht MIT Gebaeuden ist in drei Breiten
            abgenommen, und «der Export eines Projekts mit Gebaeuden darf sich
            nicht aendern» ist ein Abnahmekriterium des Pakets.
        -->
        <p v-if="!hasBuildings" class="text-xs text-gray-400 mb-2">
            {{ $t("exports.bkpSheetCount", { count: sheetCount }) }}
        </p>

        <label class="flex items-center mb-4">
            <input type="checkbox" class="rounded mr-2" v-model="hideEmpty" />
            <span class="text-sm text-gray-700">{{ $t("exports.bkpHideEmpty") }}</span>
        </label>

        <p class="text-xs uppercase font-semibold tracking-widest text-gray-500 mb-2">
            {{ $t("exports.bkpAmounts") }}
        </p>

        <label class="flex items-center mb-1">
            <input type="radio" class="mr-2" :value="true" v-model="gross" />
            <span class="text-sm text-gray-700">{{ $t("exports.bkpGross") }}</span>
        </label>

        <label class="flex items-center mb-3">
            <input type="radio" class="mr-2" :value="false" v-model="gross" />
            <span class="text-sm text-gray-700">{{ $t("exports.bkpNet") }}</span>
        </label>

        <label class="flex items-center mb-2">
            <input type="checkbox" class="rounded mr-2" v-model="onlyBilled" />
            <span class="text-sm text-gray-700">{{ $t("exports.bkpOnlyBilled") }}</span>
        </label>

        <label class="flex items-center">
            <input type="checkbox" class="rounded mr-2" v-model="withFees" />
            <span class="text-sm text-gray-700">{{ $t("exports.bkpWithFees") }}</span>
        </label>

        <p v-if="!hasSelection" class="text-xs text-red-600 mt-3">
            {{ $t("exports.bkpNoSelection") }}
        </p>
    </div>

    <div class="flex justify-end my-4 mx-6">
        <button
            @click="cancel"
            type="button"
            class="inline-block uppercase font-semibold text-xs tracking-widest"
        >
            {{ $t("actions.cancel") }}
        </button>

        <a
            :href="hasSelection ? downloadHref : null"
            class="ml-3 inline-flex items-center py-2 px-4 border border-transparent text-xs uppercase tracking-widest font-semibold text-white bg-minplan-greybeige hover:bg-minplan-lightgreybeige focus:outline-none transition"
            :class="{ 'opacity-25 pointer-events-none': !hasSelection }"
        >
            {{ $t("actions.download") }}
        </a>
    </div>
</template>

<script>
import { defineComponent } from "vue";
import MainLayout from "@/Layouts/MainLayout";
import PageTitle from "@/Shared/Components/PageTitle";

// BKP-Export (Spec `product/backlog/bkp-auswertung-export.md`, Abschnitt 4a).
// Aufbau bewusst wie `Archive.vue`: echter `<a href>`-GET-Download, der Link
// wird clientseitig aus der Auswahl gebaut; ein Klick ohne Auswahl ist über
// `pointer-events-none` und fehlendes `href` blockiert. Kein `useForm`/POST —
// Norm im Repo für Exporte.
export default defineComponent({
    props: ["project", "buildings", "unassignedCount"],
    inheritAttrs: false,
    emits: ["closeModal"],
    layout: MainLayout,
    components: { PageTitle },
    data() {
        return {
            selectedBuildings: this.collectUuids(this.buildings),
            withUnassigned: this.unassignedCount > 0,
            subgroupSheets: true,
            hideEmpty: true,
            // Auftrag Peter, 2026-09-01: Vorgabe exklusive. Die Auswertung
            // geht an Bau- und Projektleitung, die exklusive rechnen; wer
            // brutto braucht, schaltet um.
            gross: false,
            onlyBilled: true,
            withFees: false,
        };
    },
    computed: {
        // Baum → flache Liste mit Ebene, damit `v-for` einrücken kann.
        flatBuildings() {
            const flat = [];
            const walk = (nodes, level, parents) => {
                nodes.forEach((node) => {
                    flat.push({ ...node, level, parents });
                    walk(node.children || [], level + 1, [...parents, node.uuid]);
                });
            };
            walk(this.buildings || [], 0, []);
            return flat;
        },
        hasBuildings() {
            return this.flatBuildings.length > 0;
        },
        selectableUuids() {
            return this.flatBuildings.map((entry) => entry.uuid);
        },
        allSelected() {
            return (
                this.selectableUuids.length > 0 &&
                this.selectedBuildings.length === this.selectableUuids.length
            );
        },
        hasSelection() {
            return (
                this.selectedBuildings.length > 0 ||
                (this.withUnassigned && this.unassignedCount > 0)
            );
        },
        // Blattzähler: Projektblatt + je gewähltem Gebäude ein Blatt (nur mit
        // eigenen Blättern für Untergruppen) + ggf. das Blatt "ohne Gebäude".
        // Er macht sichtbar, wann eine Datei unhandlich wird.
        downloadHref() {
            return this.route("advisors.projects.bkp.export", {
                project: this.project.uuid,
                buildings: this.flatBuildings
                    .filter((entry) => this.isEffectivelySelected(entry))
                    .map((entry) => entry.uuid),
                with_unassigned: this.withUnassigned,
                subgroup_sheets: this.subgroupSheets,
                hide_empty: this.hideEmpty,
                gross: this.gross,
                only_billed: this.onlyBilled,
                with_fees: this.withFees,
            });
        },
        sheetCount() {
            // Ein Projekt ohne Gebäude hat genau ein Blatt — die Auswahl ist
            // dort leer, die Rechnung darunter also gegenstandslos.
            if (!this.hasBuildings) {
                return 1;
            }

            const selected = this.flatBuildings.filter((entry) =>
                this.isEffectivelySelected(entry)
            );
            const buildings = this.subgroupSheets
                ? selected.length
                : selected.filter((entry) => entry.level === 0).length;

            return 1 + buildings + (this.withUnassigned && this.unassignedCount ? 1 : 0);
        },
    },
    methods: {
        collectUuids(nodes) {
            return (nodes || []).flatMap((node) => [
                node.uuid,
                ...this.collectUuids(node.children),
            ]);
        },
        // Ein abgewähltes Elterngebäude nimmt seine Untergruppen mit — sonst
        // stünden Blätter in der Datei, deren Elternspalte fehlt.
        isEffectivelySelected(entry) {
            return (
                this.selectedBuildings.includes(entry.uuid) &&
                entry.parents.every((uuid) => this.selectedBuildings.includes(uuid))
            );
        },
        cascade(entry) {
            const subtree = this.flatBuildings
                .filter((candidate) => candidate.parents.includes(entry.uuid))
                .map((candidate) => candidate.uuid);

            if (this.selectedBuildings.includes(entry.uuid)) {
                this.selectedBuildings = [
                    ...new Set([...this.selectedBuildings, ...subtree]),
                ];
                return;
            }

            this.selectedBuildings = this.selectedBuildings.filter(
                (uuid) => !subtree.includes(uuid)
            );
        },
        toggleAll() {
            this.selectedBuildings = this.allSelected ? [] : [...this.selectableUuids];
        },
        cancel() {
            this.$emit("closeModal");
        },
    },
});
</script>
